import { useContext, useEffect, useState } from "react";
import { Row, Col, Card, Button, DatePicker } from "antd";
import { DownloadOutlined, CalendarOutlined, FileExcelOutlined } from "@ant-design/icons";
import { AppStateContext } from "../../context/AppContext";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";
import * as XLSX from "xlsx";

const { RangePicker } = DatePicker;

function DataExportPage() {
  const { setPageHeading } = useContext(AppStateContext);
  useEffect(() => {
    setPageHeading("Data Export");
  }, [setPageHeading]);

  return (
    <div>
      <div className="page-inner-headding">
        <DataExport />
      </div>
    </div>
  );
}

function DataExport() {
  const { request } = useRequest();
  const [attendanceExportRange, setAttendanceExportRange] = useState(null);
  const [attendanceExportLoading, setAttendanceExportLoading] = useState(false);
  const [emailDataCountExportRange, setEmailDataCountExportRange] = useState(null);
  const [emailDataCountExportLoading, setEmailDataCountExportLoading] = useState(false);
  const [distanceExportStartDate, setDistanceExportStartDate] = useState("");
  const [distanceExportEndDate, setDistanceExportEndDate] = useState("");
  const [distanceExportLoading, setDistanceExportLoading] = useState(false);

  const getDateRange = (startStr, endStr) => {
    const dates = [];
    const start = new Date(startStr);
    const end = new Date(endStr);
    const d = new Date(start);
    while (d <= end) {
      dates.push(d.toISOString().slice(0, 10));
      d.setDate(d.getDate() + 1);
    }
    return dates;
  };

  const handleExportAttendanceCSV = () => {
    if (!attendanceExportRange || attendanceExportRange.length !== 2) {
      ShowToast("Please select Start date and End date", Severty.WARNING);
      return;
    }
    const [start, end] = attendanceExportRange;
    const start_date = start ? start.format("YYYY-MM-DD") : "";
    const end_date = end ? end.format("YYYY-MM-DD") : "";
    if (!start_date || !end_date) {
      ShowToast("Please select Start date and End date", Severty.WARNING);
      return;
    }
    setAttendanceExportLoading(true);
    const query = new URLSearchParams({ start_date, end_date });
    request({
      url: `${apiPath.attendanceRegisterExport}?${query.toString()}`,
      method: "GET",
      onSuccess: ({ data }) => {
        const list = data?.list || [];
        const dateColumns = getDateRange(start_date, end_date);
        const baseHeaders = ["Name", "Employee ID", "Email", "Mobile"];
        const headers = [...baseHeaders, ...dateColumns, "Total Working Hours"];
        const escapeCsv = (val) => {
          if (val == null) return "";
          const s = String(val);
          if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
          return s;
        };
        const attendanceSet = (row) => new Set(row.attendanceDates || []);
        const workingHours = (row) =>
          Object.fromEntries(
            (row.workingHoursByDate || []).map((item) => [item.date, Number(item.minutes) || 0])
          );
        const formatMinutes = (minutes) => {
          if (minutes == null) return "";
          const hours = Math.floor(minutes / 60);
          const remainingMinutes = minutes % 60;
          return hours ? `${hours}h ${remainingMinutes}m` : `${remainingMinutes}m`;
        };
        const rows = list.map((row) => {
          const present = attendanceSet(row);
          const hours = workingHours(row);
          const dayCells = dateColumns.map((d) => {
            if (!present.has(d)) return "";
            const duration = formatMinutes(hours[d]);
            return duration ? `✓ (${duration})` : "✓";
          });
          const totalMinutes = Object.values(hours).reduce((sum, value) => sum + value, 0);
          return [
            escapeCsv(row.name),
            escapeCsv(row.suid),
            escapeCsv(row.email),
            escapeCsv(row.mobileNumber),
            ...dayCells,
            escapeCsv(formatMinutes(totalMinutes)),
          ];
        });
        const csvContent = [
          headers.join(","),
          ...rows.map((r) => r.join(",")),
        ].join("\n");
        const blob = new Blob(["\uFEFF" + csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `attendance-register-${start_date}-to-${end_date}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
        ShowToast("Attendance CSV saved locally", Severty.SUCCESS);
        setAttendanceExportLoading(false);
      },
      onError: (err) => {
        ShowToast(err?.message || "Export failed", Severty.ERROR);
        setAttendanceExportLoading(false);
      },
    });
  };

  const handleExportEmailDataCountCSV = () => {
    if (!emailDataCountExportRange || emailDataCountExportRange.length !== 2) {
      ShowToast("Please select Start date and End date", Severty.WARNING);
      return;
    }
    const [start, end] = emailDataCountExportRange;
    const start_date = start ? start.format("YYYY-MM-DD") : "";
    const end_date = end ? end.format("YYYY-MM-DD") : "";
    if (!start_date || !end_date) {
      ShowToast("Please select Start date and End date", Severty.WARNING);
      return;
    }
    setEmailDataCountExportLoading(true);
    const query = new URLSearchParams({ start_date, end_date });
    request({
      url: `${apiPath.emailDataCountExport}?${query.toString()}`,
      method: "GET",
      onSuccess: ({ data }) => {
        const list = data?.list || [];
        const dateColumns = getDateRange(start_date, end_date);
        const baseHeaders = ["Name", "Employee ID", "Email", "Mobile"];
        const headers = [...baseHeaders, ...dateColumns];
        const escapeCsv = (val) => {
          if (val == null) return "";
          const s = String(val);
          if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
          return s;
        };
        const countByDate = (row) => {
          const arr = row.emailDataCountByDate || [];
          const map = {};
          arr.forEach((o) => {
            map[o.dateStr] = o.count;
          });
          return map;
        };
        const rows = list.map((row) => {
          const counts = countByDate(row);
          const dayCells = dateColumns.map((d) => counts[d] ?? 0);
          return [
            escapeCsv(row.name),
            escapeCsv(row.suid),
            escapeCsv(row.email),
            escapeCsv(row.mobileNumber),
            ...dayCells,
          ];
        });
        const csvContent = [
          headers.join(","),
          ...rows.map((r) => r.join(",")),
        ].join("\n");
        const blob = new Blob(["\uFEFF" + csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `email-data-count-${start_date}-to-${end_date}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
        ShowToast("Email data count CSV saved locally", Severty.SUCCESS);
        setEmailDataCountExportLoading(false);
      },
      onError: (err) => {
        ShowToast(err?.message || "Export failed", Severty.ERROR);
        setEmailDataCountExportLoading(false);
      },
    });
  };

  const handleExportDistanceExcel = () => {
    if (!distanceExportStartDate || !distanceExportEndDate) {
      ShowToast("Please select Start date and End date", Severty.WARNING);
      return;
    }
    const start_date = distanceExportStartDate;
    const end_date = distanceExportEndDate;
    if (start_date > end_date) {
      ShowToast("End date must be after Start date", Severty.WARNING);
      return;
    }
    if (!start_date || !end_date) {
      ShowToast("Please select Start date and End date", Severty.WARNING);
      return;
    }

    setDistanceExportLoading(true);
    const query = new URLSearchParams({ start_date, end_date });
    request({
      url: `${apiPath.distanceTravelledExport}?${query.toString()}`,
      method: "GET",
      onSuccess: ({ data }) => {
        const rows = (data?.list || []).map((row, index) => ({
          "#": index + 1,
          Name: row.name || "",
          "Employee ID": row.suid || "",
          Email: row.email || "",
          Mobile: row.mobileNumber || "",
          "GPS Points": Number(row.locationCount) || 0,
          "Total Distance (km)": Number(row.totalDistanceKm) || 0,
          "First Location": row.firstLocationAt
            ? new Date(row.firstLocationAt).toLocaleString()
            : "",
          "Last Location": row.lastLocationAt
            ? new Date(row.lastLocationAt).toLocaleString()
            : "",
        }));
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Distance Travelled");
        XLSX.writeFile(
          workbook,
          `distance-travelled-${start_date}-to-${end_date}.xlsx`
        );
        ShowToast("Distance travelled Excel saved locally", Severty.SUCCESS);
        setDistanceExportLoading(false);
      },
      onError: (err) => {
        ShowToast(err?.message || "Export failed", Severty.ERROR);
        setDistanceExportLoading(false);
      },
    });
  };

  return (
    <Row gutter={[24, 0]}>
      <Col xs={24} xl={24}>
        <Card
          bordered
          className="criclebox tablespace mb-24"
          title={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <CalendarOutlined />
              Attendance Export
            </span>
          }
          style={{ marginBottom: 24 }}
          bodyStyle={{ padding: "20px 24px" }}
        >
          <p style={{ marginBottom: 16, color: "#595959", fontSize: 13 }}>
            Pick start and end date. CSV with day-wise attendance (from EmailData) will download to your device.
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 12,
              padding: "12px 16px",
              background: "#fafafa",
              borderRadius: 8,
              border: "1px solid #f0f0f0",
            }}
          >
            <RangePicker
              value={attendanceExportRange}
              onChange={(dates) => setAttendanceExportRange(dates)}
              allowClear
              size="large"
              style={{ minWidth: 260 }}
              placeholder={["Start date", "End date"]}
              format="DD MMM YYYY"
            />
            <Button
              type="primary"
              size="large"
              icon={<DownloadOutlined />}
              onClick={handleExportAttendanceCSV}
              loading={attendanceExportLoading}
              style={{ minWidth: 160 }}
            >
              {attendanceExportLoading ? "Exporting…" : "Export CSV"}
            </Button>
          </div>
        </Card>
        <Card
          bordered
          className="criclebox tablespace mb-24"
          title={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <DownloadOutlined />
              Email Data Count Export
            </span>
          }
          style={{ marginBottom: 24 }}
          bodyStyle={{ padding: "20px 24px" }}
        >
          <p style={{ marginBottom: 16, color: "#595959", fontSize: 13 }}>
            Pick start and end date. CSV with each user's total number of EmailData records per day will download.
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 12,
              padding: "12px 16px",
              background: "#fafafa",
              borderRadius: 8,
              border: "1px solid #f0f0f0",
            }}
          >
            <RangePicker
              value={emailDataCountExportRange}
              onChange={(dates) => setEmailDataCountExportRange(dates)}
              allowClear
              size="large"
              style={{ minWidth: 260 }}
              placeholder={["Start date", "End date"]}
              format="DD MMM YYYY"
            />
            <Button
              type="primary"
              size="large"
              icon={<DownloadOutlined />}
              onClick={handleExportEmailDataCountCSV}
              loading={emailDataCountExportLoading}
              style={{ minWidth: 160 }}
            >
              {emailDataCountExportLoading ? "Exporting…" : "Export CSV"}
            </Button>
          </div>
        </Card>
        <Card
          bordered
          className="criclebox tablespace mb-24"
          title={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <FileExcelOutlined />
              Distance Travelled Export
            </span>
          }
          style={{ marginBottom: 24 }}
          bodyStyle={{ padding: "20px 24px" }}
        >
          <p style={{ marginBottom: 16, color: "#595959", fontSize: 13 }}>
            Pick start and end date. Excel will contain each user's total GPS distance travelled between consecutive locations.
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 12,
              padding: "12px 16px",
              background: "#fafafa",
              borderRadius: 8,
              border: "1px solid #f0f0f0",
            }}
          >
            <input
              type="date"
              value={distanceExportStartDate}
              onChange={(event) => setDistanceExportStartDate(event.target.value)}
              aria-label="Distance export start date"
              style={{
                height: 40,
                minWidth: 170,
                padding: "0 12px",
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                color: "#595959",
                background: "#fff",
              }}
            />
            <span style={{ color: "#8c8c8c" }}>to</span>
            <input
              type="date"
              value={distanceExportEndDate}
              onChange={(event) => setDistanceExportEndDate(event.target.value)}
              aria-label="Distance export end date"
              style={{
                height: 40,
                minWidth: 170,
                padding: "0 12px",
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                color: "#595959",
                background: "#fff",
              }}
            />
            <Button
              type="primary"
              size="large"
              icon={<FileExcelOutlined />}
              onClick={handleExportDistanceExcel}
              loading={distanceExportLoading}
              style={{ minWidth: 160 }}
            >
              {distanceExportLoading ? "Exporting…" : "Export Excel"}
            </Button>
          </div>
        </Card>
      </Col>
    </Row>
  );
}

export default DataExportPage;
